import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  CheckCircle,
  Filter,
  FileText,
  Edit,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SortableTableHeader from '../components/SortableTableHeader';
import VoidConfirmationModal from '../components/VoidConfirmationModal';
import DateRangeSelect from '../components/DateRangeSelect';
import TransactionListTotalsBar from '../components/transactions/TransactionListTotalsBar';
import RowActionsMenu from '../components/transactions/RowActionsMenu';
import { getPurchaseTransactions } from '../utils/purchaseTransactionApi';
import { buildPurchaseBillRowActions } from '../utils/purchaseRowActions';
import { deleteBill, receiveBill, voidBill } from '../utils/billApi';
import type {
  PurchaseTransaction,
  PurchaseTransactionSummary,
} from '../types/purchaseTransaction';
import type { PaginationMeta } from '../types/journalEntry';
import { formatCurrency } from '../utils/formatCurrency';
import { computePurchaseTransactionTotals } from '../utils/purchaseTransactionSelection';
import { toast, toastRouteSuccess, clearRouteState } from '../utils/toast';
import { billVoidCopy } from '../utils/voidConfirmationCopy';
import {
  DEFAULT_DATE_RANGE_PRESET,
  getDateRangeForPreset,
  type DateRangePreset,
} from '../utils/dateRangePresets';
import {
  encodePurchasesSort,
  loadPurchasesTablePreferences,
  parsePurchasesSort,
  savePurchasesTablePreferences,
  type PurchasesSortField,
  type SortDir,
} from '../utils/purchasesTablePreferences';

type StatusFilter = 'all' | 'open' | 'overdue' | 'draft' | 'partial' | 'paid' | 'voided';
type SortKey = PurchasesSortField;

const defaultDateRange = getDateRangeForPreset(DEFAULT_DATE_RANGE_PRESET);

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const formatTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const compareBills = (
  a: PurchaseTransaction,
  b: PurchaseTransaction,
  key: SortKey,
  dir: SortDir
) => {
  const mul = dir === 'asc' ? 1 : -1;

  switch (key) {
    case 'number':
    case 'vendor_name':
    case 'status':
      return mul * a[key].localeCompare(b[key]);
    case 'total_amount':
    case 'balance':
      return mul * (parseFloat(a[key]) - parseFloat(b[key]));
    case 'txn_date':
      return (
        mul *
        (a.txn_date.localeCompare(b.txn_date) || a.created_at.localeCompare(b.created_at))
      );
    case 'received_into_inventory':
      return mul * (Number(a.received_into_inventory) - Number(b.received_into_inventory));
    default:
      return 0;
  }
};

const defaultSortDir = (key: SortKey): SortDir =>
  key === 'txn_date' || key === 'total_amount' || key === 'balance' ? 'desc' : 'asc';

const canManageBills = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteOrVoid = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const getStatusColor = (status: string, isOverdue = false) => {
  if (isOverdue) return 'bg-orange-100 text-orange-800';
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'draft', label: 'Draft' },
  { id: 'partial', label: 'Partial' },
  { id: 'paid', label: 'Paid' },
  { id: 'voided', label: 'Voided' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const Purchases = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bills, setBills] = useState<PurchaseTransaction[]>([]);
  const [summary, setSummary] = useState<PurchaseTransactionSummary | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DateRangePreset>(DEFAULT_DATE_RANGE_PRESET);
  const [startDate, setStartDate] = useState(defaultDateRange.startDate);
  const [endDate, setEndDate] = useState(defaultDateRange.endDate);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() => loadPurchasesTablePreferences().perPage);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const parsed = parsePurchasesSort(loadPurchasesTablePreferences().sort);
    return parsed?.field ?? 'txn_date';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const parsed = parsePurchasesSort(loadPurchasesTablePreferences().sort);
    return parsed?.direction ?? 'desc';
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [voidTarget, setVoidTarget] = useState<PurchaseTransaction | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    savePurchasesTablePreferences({
      sort: encodePurchasesSort(sortKey, sortDir),
      perPage,
    });
  }, [sortKey, sortDir, perPage]);

  const sortedBills = useMemo(
    () => [...bills].sort((a, b) => compareBills(a, b, sortKey, sortDir)),
    [bills, sortKey, sortDir]
  );

  const columnTotals = useMemo(
    () => computePurchaseTransactionTotals(sortedBills),
    [sortedBills]
  );

  const visibleRowKeys = useMemo(
    () => sortedBills.map((txn) => String(txn.id)),
    [sortedBills]
  );

  const selectedRows = useMemo(
    () => sortedBills.filter((txn) => selectedKeys.has(String(txn.id))),
    [sortedBills, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computePurchaseTransactionTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleSort = (column: SortKey) => {
    if (sortKey === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir(defaultSortDir(column));
    }
  };

  const fetchBills = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPurchaseTransactions({
        type: 'bill',
        status: statusFilter === 'all' ? undefined : statusFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: searchQuery || undefined,
        paid_days: 30,
        per_page: perPage,
        page: pageNum,
      });
      setBills(result.transactions);
      setSummary(result.summary);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch bills');
      setBills([]);
      setSummary(null);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (datePreset === 'custom' && (!startDate || !endDate)) {
      return;
    }
    fetchBills(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startDate, endDate, searchQuery, datePreset, perPage]);

  useEffect(() => {
    const state = location.state as { successMessage?: string; statusFilter?: StatusFilter } | null;
    toastRouteSuccess(state);
    if (state?.statusFilter) {
      setStatusFilter(state.statusFilter);
      setPage(1);
    }
    if (state?.successMessage || state?.statusFilter) {
      clearRouteState();
    }
  }, [location.state]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [statusFilter, startDate, endDate, searchQuery, datePreset, perPage, page]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleReceive = async (txn: PurchaseTransaction) => {
    if (!canManageBills()) return;
    if (!window.confirm(`Receive bill ${txn.number} into inventory and post GL?`)) return;

    try {
      setActionLoading(txn.id);
      await receiveBill(txn.id);
      await fetchBills(page);
      toast.success(`Bill ${txn.number} received.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to receive bill');
    } finally {
      setActionLoading(null);
    }
  };

  const openVoidModal = (txn: PurchaseTransaction) => {
    if (!canDeleteOrVoid()) return;
    setVoidError(null);
    setVoidTarget(txn);
  };

  const cancelVoidModal = () => {
    setVoidTarget(null);
    setVoidError(null);
  };

  const confirmVoid = async () => {
    if (!voidTarget || !canDeleteOrVoid()) return;

    try {
      setVoiding(true);
      setVoidError(null);
      setActionLoading(voidTarget.id);
      await voidBill(voidTarget.id);
      await fetchBills(page);
      toast.success(`Bill ${voidTarget.number} voided.`);
      setVoidTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void bill';
      setVoidError(message);
      toast.error(message);
    } finally {
      setVoiding(false);
      setActionLoading(null);
    }
  };

  const handleDelete = async (txn: PurchaseTransaction) => {
    if (!canDeleteOrVoid()) return;
    if (!window.confirm(`Delete draft ${txn.number}? This cannot be undone.`)) return;

    try {
      setActionLoading(txn.id);
      await deleteBill(txn.id);
      await fetchBills(page);
      toast.success(`Bill ${txn.number} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete bill');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDatePresetChange = (preset: DateRangePreset) => {
    setDatePreset(preset);
    setPage(1);

    if (preset === 'custom') {
      if (!startDate || !endDate) {
        const fallback = getDateRangeForPreset('today');
        setStartDate(fallback.startDate);
        setEndDate(fallback.endDate);
      }
      return;
    }

    const range = getDateRangeForPreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleCustomDateRangeChange = (from: string, to: string) => {
    setStartDate(from);
    setEndDate(to);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setDatePreset(DEFAULT_DATE_RANGE_PRESET);
    setStartDate(defaultDateRange.startDate);
    setEndDate(defaultDateRange.endDate);
    setStatusFilter('all');
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(searchQuery) || statusFilter !== 'all' || datePreset !== DEFAULT_DATE_RANGE_PRESET;

  const summaryCards = summary
    ? [
        {
          key: 'open' as const,
          label: 'Open',
          bucket: summary.open,
          status: 'open' as StatusFilter,
          icon: FileText,
          accent: 'border-amber-200 bg-amber-50',
          text: 'text-amber-900',
        },
        {
          key: 'overdue' as const,
          label: 'Overdue',
          bucket: summary.overdue,
          status: 'overdue' as StatusFilter,
          icon: AlertTriangle,
          accent: 'border-orange-200 bg-orange-50',
          text: 'text-orange-800',
        },
        {
          key: 'draft' as const,
          label: 'Draft',
          bucket: summary.draft,
          status: 'draft' as StatusFilter,
          icon: Edit,
          accent: 'border-gray-200 bg-gray-50',
          text: 'text-gray-800',
        },
        {
          key: 'paid_recent' as const,
          label: `Paid (${summary.paid_recent.days ?? 30}d)`,
          bucket: summary.paid_recent,
          status: 'paid' as StatusFilter,
          icon: CheckCircle,
          accent: 'border-green-200 bg-green-50',
          text: 'text-green-800',
        },
      ]
    : [];

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : columnTotals;
  const totalsBarPageSuffix =
    meta && meta.total > columnTotals.count ? ' on this page' : '';
  const showTotalsBar = sortedBills.length > 0;

  if (loading && bills.length === 0) {
    return (
      <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
        <Sidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
          <div className="flex flex-1 items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Bills</h1>
                <p className="mt-1 text-sm text-gray-500">Vendor bills — draft, receive, and pay</p>
              </div>
              {canManageBills() && (
                <Link
                  to="/manage-purchases"
                  className="flex items-center justify-center px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Bill
                </Link>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchBills(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          {summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {summaryCards.map((card) => {
                const Icon = card.icon;
                const active = statusFilter === card.status;
                return (
                  <button
                    key={card.key}
                    type="button"
                    onClick={() => {
                      setStatusFilter(card.status);
                      setPage(1);
                    }}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${card.accent} ${
                      active ? 'ring-2 ring-slate-800 ring-offset-1' : 'hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${card.text}`}>{card.label}</span>
                      <Icon className={`h-4 w-4 ${card.text}`} />
                    </div>
                    <p className={`text-2xl font-semibold ${card.text}`}>
                      {formatCurrency(card.bucket.total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {card.bucket.count} {card.bucket.count === 1 ? 'bill' : 'bills'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mb-4 flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setStatusFilter(tab.id);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === tab.id
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Bill #, vendor..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800"
                  />
                </div>
              </div>
              <DateRangeSelect
                preset={datePreset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={handleDatePresetChange}
                onCustomRangeChange={handleCustomDateRangeChange}
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700"
              >
                Search
              </button>
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {meta?.total ?? bills.length} bills
                  </span>
                  <button type="button" onClick={clearFilters} className="text-sm text-slate-700 underline">
                    Clear
                  </button>
                </>
              )}
            </div>
          </form>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader label="Date" column="txn_date" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Bill" column="number" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Vendor" column="vendor_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Total" column="total_amount" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Balance" column="balance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Status" column="status" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Received" column="received_into_inventory" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedBills.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">No bills found.</p>
                    </td>
                  </tr>
                ) : (
                  sortedBills.map((txn) => {
                    const balance = parseFloat(txn.balance);
                    const busy = actionLoading === txn.id;
                    const rowKey = String(txn.id);
                    const isCheckboxSelected = selectedKeys.has(rowKey);
                    return (
                      <tr
                        key={txn.id}
                        className={`hover:bg-gray-50 cursor-pointer${
                          isCheckboxSelected ? ' bg-slate-50/60' : ''
                        }`}
                        onClick={() => navigate(`/purchases/${txn.id}`)}
                      >
                        <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isCheckboxSelected}
                            onChange={(e) => toggleRow(rowKey, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-slate-800"
                            aria-label={`Select ${txn.number}`}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>{formatDate(txn.txn_date)}</div>
                          {txn.created_at && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatTime(txn.created_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{txn.number}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{txn.vendor_name || '—'}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">
                          {formatCurrency(txn.total_amount)}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm text-right font-medium ${
                            balance > 0 ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {formatCurrency(txn.balance)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(txn.status, txn.is_overdue)}`}
                          >
                            {txn.is_overdue ? 'Overdue' : formatLabel(txn.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              txn.received_into_inventory
                                ? 'bg-green-50 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {txn.received_into_inventory ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            {...buildPurchaseBillRowActions(txn, {
                              onView: () => navigate(`/purchases/${txn.id}`),
                              onEdit: () => navigate(`/manage-purchases?id=${txn.id}`),
                              onReceive: () => void handleReceive(txn),
                              onDelete: () => void handleDelete(txn),
                              onVoid: () => openVoidModal(txn),
                              onPayBill: () =>
                                navigate('/pay-bills', { state: { vendorId: txn.vendor_id } }),
                            })}
                            disabled={busy}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
              </div>
            )}
          </div>

          {meta && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <label htmlFor="bills-per-page" className="text-gray-700">
                  Rows per page
                </label>
                <select
                  id="bills-per-page"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <span>
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </span>
              {meta.last_page > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchBills(meta.current_page - 1)}
                    disabled={meta.current_page <= 1 || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchBills(meta.current_page + 1)}
                    disabled={meta.current_page >= meta.last_page || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <TransactionListTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              totalAmount={totalsBarData.totalAmount}
              balance={totalsBarData.balance}
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>

      {voidTarget && (
        <VoidConfirmationModal
          isOpen={voidTarget != null}
          {...billVoidCopy(voidTarget.number)}
          isSubmitting={voiding}
          error={voidError}
          onCancel={cancelVoidModal}
          onConfirm={confirmVoid}
        />
      )}
    </div>
  );
};

export default Purchases;
